import React, { useState, useEffect } from "react";
import { Phone, MessageCircle, Menu, X, ChevronDown } from "lucide-react";
import { LOGO_URL, CONTACT } from "../data/mock";

const NAV_LINKS = [
  { label: "Designs", href: "#designs" },
  { label: "Services", href: "#services" },
  { label: "3D Design", href: "#3d-design" },
  { label: "Process", href: "#process" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "#contact" },
];

export function Navbar({ onGetQuote }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const handleNav = (href) => {
    setMenuOpen(false);
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = href;
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-lg" : "bg-white/95 backdrop-blur-sm"
      }`}
    >
      {/* Top bar */}
      <div className="bg-[#1a1a1a] text-white text-xs py-1.5 px-4 flex justify-between items-center">
        <span className="hidden sm:block">Hyderabad's Most Trusted Interior Designers | Good Design, Better Living</span>
        <div className="flex items-center gap-4 ml-auto">
          <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-1 hover:text-[#C9A84C] transition-colors">
            <Phone size={11} />
            <span>{CONTACT.phone}</span>
          </a>
          <a
            href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(CONTACT.whatsappMsg)}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 hover:text-[#25D366] transition-colors"
          >
            <MessageCircle size={11} />
            <span>WhatsApp</span>
          </a>
        </div>
      </div>

      {/* Main nav */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <a href="/" className="flex-shrink-0" data-testid="nav-logo">
          <img
            src={LOGO_URL}
            alt="Good Idea Interiors"
            className="h-12 w-auto object-contain"
          />
        </a>

        {/* Desktop Links */}
        <ul className="hidden lg:flex items-center gap-6">
          {NAV_LINKS.map((link) => (
            <li key={link.label}>
              <button
                onClick={() => handleNav(link.href)}
                className="text-[#2D2D2D] hover:text-[#C9A84C] font-medium text-sm transition-colors relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#C9A84C] group-hover:w-full transition-all duration-300" />
              </button>
            </li>
          ))}
        </ul>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${CONTACT.phone}`}
            className="flex items-center gap-2 text-sm font-medium text-[#1a1a1a] border border-[#1a1a1a] px-4 py-2 rounded-full hover:bg-[#1a1a1a] hover:text-white transition-all"
            data-testid="nav-call-btn"
          >
            <Phone size={14} />
            Call Now
          </a>
          <button
            onClick={onGetQuote}
            className="bg-[#C9A84C] text-white text-sm font-semibold px-5 py-2 rounded-full hover:bg-[#b8933e] transition-colors"
            data-testid="nav-quote-btn"
          >
            Get Free Quote
          </button>
        </div>

        {/* Mobile menu button */}
        <button
          className="lg:hidden p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          data-testid="mobile-menu-btn"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-4 pb-4" data-testid="mobile-menu">
          {NAV_LINKS.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNav(link.href)}
              className="block w-full text-left py-3 text-[#2D2D2D] font-medium border-b border-gray-50 hover:text-[#C9A84C]"
            >
              {link.label}
            </button>
          ))}
          <div className="flex gap-3 mt-4">
            <a
              href={`tel:${CONTACT.phone}`}
              className="flex-1 text-center text-sm font-medium border border-[#1a1a1a] py-2.5 rounded-full"
            >
              Call Now
            </a>
            <button
              onClick={onGetQuote}
              className="flex-1 bg-[#C9A84C] text-white text-sm font-semibold py-2.5 rounded-full"
            >
              Get Free Quote
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
